// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, ShoppingCart } from 'lucide-react';
import { DEFAULT_COACH_FIELDS } from '@/types/events';
import { formatUsPhone, phoneDigits } from '@/lib/phone';

import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface Props {
  eventId: string;
  eventName: string;
  onSuccess?: () => void;
  // Legacy/unused props kept for compatibility with EventPage call site:
  attendanceFee?: number;
  couponCode?: string;
  instructions?: string;
}

type FieldConfig = {
  key: string;
  label: string;
  type: string;
  required: boolean;
  placeholder?: string;
  helper_text?: string;
  options?: string[];
  order: number;
};

type PricingRow = {
  id: string;
  division: string;
  base_price: number;
  hospitality_per_day_price: number;
  additional_coach_price: number;
  is_active: boolean;
  display_order: number;
};

const fmt = (n: number) => `$${(Number(n) || 0).toFixed(2)}`;

export const CoachAttendanceForm: React.FC<Props> = ({ eventId, eventName, onSuccess }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [fields, setFields] = useState<FieldConfig[]>([]);
  const [formTitle, setFormTitle] = useState<string | null>(null);
  const [formDescription, setFormDescription] = useState<string | null>(null);
  const [pricing, setPricing] = useState<PricingRow[]>([]);
  const [chargePaid, setChargePaid] = useState(true);

  const [responses, setResponses] = useState<Record<string, any>>({});
  const [hideFromPublic, setHideFromPublic] = useState(false);

  /* ──────── Load admin-configured form + pricing ──────── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [{ data: cfg }, { data: prices }, { data: ev }] = await Promise.all([
        supabase
          .from('event_form_configs')
          .select('fields, form_title, form_description')
          .eq('event_id', eventId)
          .eq('form_type', 'coach_attendance')
          .maybeSingle(),
        supabase
          .from('event_coach_pricing')
          .select('*')
          .eq('event_id', eventId)
          .eq('is_active', true)
          .order('display_order'),
        supabase
          .from('events')
          .select('coach_attendance_paid')
          .eq('id', eventId)
          .maybeSingle(),
      ]);
      if (cancelled) return;

      const list: FieldConfig[] =
        (cfg?.fields as FieldConfig[])?.length ? (cfg.fields as FieldConfig[]) : (DEFAULT_COACH_FIELDS as any);
      list.sort((a, b) => (a.order || 0) - (b.order || 0));
      setFields(list);
      setFormTitle(cfg?.form_title ?? null);
      setFormDescription(cfg?.form_description ?? null);
      setPricing((prices ?? []) as PricingRow[]);
      setChargePaid((ev as any)?.coach_attendance_paid !== false);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  /* ──────── Selected division & cart math ──────── */
  const divisionField = useMemo(() => fields.find((f) => f.key === 'division'), [fields]);
  const hospitalityField = useMemo(() => fields.find((f) => f.key === 'hospitality_days'), [fields]);
  const additionalField = useMemo(() => fields.find((f) => f.key === 'additional_coaches'), [fields]);

  const selectedDivision: string | undefined =
    (responses.division as string) || pricing[0]?.division;

  const pricingRow = useMemo(
    () => pricing.find((p) => p.division === selectedDivision),
    [pricing, selectedDivision]
  );

  const hospitalityCount = useMemo(() => {
    const v = responses.hospitality_days;
    if (Array.isArray(v)) return v.length;
    if (typeof v === 'string' && v) return 1;
    return 0;
  }, [responses.hospitality_days]);

  const additionalCount = useMemo(() => {
    const v = responses.additional_coaches;
    return Array.isArray(v) ? v.filter((c: any) => c?.name?.trim()).length : 0;
  }, [responses.additional_coaches]);

  const lineItems = useMemo(() => {
    if (!chargePaid || !pricingRow) return [];
    const items: { label: string; amount: number }[] = [];
    if (pricingRow.base_price > 0) {
      items.push({ label: `${pricingRow.division} coach attendance`, amount: pricingRow.base_price });
    }
    if (hospitalityCount > 0 && pricingRow.hospitality_per_day_price > 0) {
      items.push({
        label: `Hospitality × ${hospitalityCount} day${hospitalityCount > 1 ? 's' : ''}`,
        amount: pricingRow.hospitality_per_day_price * hospitalityCount,
      });
    }
    if (additionalCount > 0 && pricingRow.additional_coach_price > 0) {
      items.push({
        label: `Additional coach × ${additionalCount}`,
        amount: pricingRow.additional_coach_price * additionalCount,
      });
    }
    return items;
  }, [chargePaid, pricingRow, hospitalityCount, additionalCount]);

  const totalDollars = useMemo(
    () => lineItems.reduce((s, i) => s + i.amount, 0),
    [lineItems]
  );

  /* ──────── Field renderer ──────── */
  const setVal = (key: string, value: any) =>
    setResponses((p) => ({ ...p, [key]: value }));

  const renderField = (f: FieldConfig) => {
    const v = responses[f.key];
    switch (f.type) {
      case 'textarea':
        return (
          <Textarea
            value={v ?? ''}
            onChange={(e) => setVal(f.key, e.target.value)}
            placeholder={f.placeholder}
            rows={3}
          />
        );
      case 'select':
        return (
          <Select value={v ?? ''} onValueChange={(val) => setVal(f.key, val)}>
            <SelectTrigger><SelectValue placeholder={f.placeholder || 'Select…'} /></SelectTrigger>
            <SelectContent>
              {(f.options ?? []).map((opt) => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'multi_select': {
        const arr: string[] = Array.isArray(v) ? v : [];
        return (
          <div className="grid grid-cols-2 gap-2">
            {(f.options ?? []).map((opt) => {
              const checked = arr.includes(opt);
              return (
                <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(c) => {
                      const next = c
                        ? [...arr, opt]
                        : arr.filter((x) => x !== opt);
                      setVal(f.key, next);
                    }}
                  />
                  {opt}
                </label>
              );
            })}
          </div>
        );
      }
      case 'checkbox':
        return (
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={!!v} onCheckedChange={(c) => setVal(f.key, !!c)} />
            {f.placeholder || f.label}
          </label>
        );
      case 'repeater': {
        const rows: { name: string; email?: string }[] = Array.isArray(v) ? v : [];
        return (
          <div className="space-y-2">
            {rows.map((row, i) => (
              <div key={i} className="flex gap-2 items-start">
                <Input
                  value={row.name || ''}
                  placeholder="Coach name"
                  onChange={(e) => {
                    const next = [...rows];
                    next[i] = { ...next[i], name: e.target.value };
                    setVal(f.key, next);
                  }}
                />
                <Input
                  value={row.email || ''}
                  placeholder="Email (optional)"
                  onChange={(e) => {
                    const next = [...rows];
                    next[i] = { ...next[i], email: e.target.value };
                    setVal(f.key, next);
                  }}
                />
                <Button
                  type="button" variant="ghost" size="icon"
                  onClick={() => setVal(f.key, rows.filter((_, j) => j !== i))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button" variant="outline" size="sm"
              onClick={() => setVal(f.key, [...rows, { name: '', email: '' }])}
            >
              <Plus className="h-3 w-3 mr-1" /> Add coach
            </Button>
          </div>
        );
      }
      case 'email':
        return <Input type="email" value={v ?? ''} placeholder={f.placeholder} onChange={(e) => setVal(f.key, e.target.value)} />;
      case 'phone':
        return (
          <Input
            type="tel"
            inputMode="tel"
            value={formatUsPhone(v ?? '')}
            placeholder={f.placeholder || '(816) 223-7452'}
            onChange={(e) => setVal(f.key, phoneDigits(e.target.value))}
          />
        );

      case 'number':
        return <Input type="number" value={v ?? ''} placeholder={f.placeholder} onChange={(e) => setVal(f.key, e.target.value)} />;
      default:
        return <Input value={v ?? ''} placeholder={f.placeholder} onChange={(e) => setVal(f.key, e.target.value)} />;
    }
  };

  /* ──────── Submit ──────── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    for (const f of fields) {
      if (!f.required) continue;
      const v = responses[f.key];
      const empty =
        v == null ||
        (typeof v === 'string' && !v.trim()) ||
        (Array.isArray(v) && v.length === 0);
      if (empty) {
        toast.error(`${f.label} is required`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const fillerName = String(responses.filler_name || responses.coach_name || '').trim();
      const fillerEmail = String(responses.filler_email || responses.coach_email || '').trim();
      const coachName = String(responses.coach_name || fillerName).trim();
      const collegeName = String(responses.college_name || '').trim();
      const division = String(responses.division || pricingRow?.division || '').trim();
      const coachEmail = String(responses.coach_email || fillerEmail).trim();
      const coachPhone = String(responses.coach_phone || '').trim();

      const totalCents = Math.round(totalDollars * 100);

      const { data: coachRecord, error: insertError } = await supabase
        .from('event_coaches')
        .insert({
          event_id: eventId,
          coach_name: coachName || 'Unknown',
          college_name: collegeName || null,
          division: division || null,
          coach_email: coachEmail || null,
          coach_phone: coachPhone || null,
          filler_name: fillerName || null,
          filler_email: fillerEmail || null,
          form_data: responses,
          hide_from_public: hideFromPublic,
          payment_status: totalCents > 0 ? 'pending' : (chargePaid ? 'unpaid' : 'comp'),
          amount_paid_cents: 0,
        })
        .select()
        .single();

      if (insertError) {
        console.error('Coach insert failed:', insertError);
        toast.error('Failed to register: ' + insertError.message);
        return;
      }

      if (totalCents > 0) {
        const { data: checkout, error: ckErr } = await invokeEdgeFunction(
          'create-coach-attendance-checkout',
          {
            body: {
              eventId,
              eventCoachId: coachRecord.id,
              amount: totalCents,
              coachEmail: coachEmail || fillerEmail,
              coachName,
              collegeName,
            },
          }
        );
        if (ckErr || !checkout?.url) {
          toast.error('Failed to start checkout');
          return;
        }
        window.location.href = checkout.url;
        return;
      }

      toast.success('Registration submitted!');
      onSuccess?.();
      navigate(`/coach-registration-confirmation?id=${coachRecord.id}&event_id=${eventId}`);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || 'Unexpected error');
    } finally {
      setSubmitting(false);
    }
  };

  /* ──────── Render ──────── */
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {(formTitle || formDescription) && (
        <div className="space-y-1">
          {formTitle && <h3 className="text-lg font-semibold">{formTitle}</h3>}
          {formDescription && (
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{formDescription}</p>
          )}
        </div>
      )}

      {/* Cart summary — only when there's something to charge */}
      {chargePaid && pricing.length > 0 && (
        <Card className="border-cyan/30 bg-cyan/5">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <ShoppingCart className="h-4 w-4" /> Order Summary
            </div>
            {lineItems.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {selectedDivision
                  ? 'Select options above to see your total.'
                  : 'Choose a division to see pricing.'}
              </p>
            ) : (
              <ul className="text-sm space-y-1">
                {lineItems.map((li, i) => (
                  <li key={i} className="flex justify-between">
                    <span>{li.label}</span>
                    <span>{fmt(li.amount)}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="border-t pt-2 flex justify-between font-semibold">
              <span>Total</span>
              <span>{fmt(totalDollars)}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Division selector — driven by admin pricing rows */}
      {pricing.length > 0 && (
        <div className="space-y-1.5">
          <Label htmlFor="division">
            Division<span className="text-destructive ml-1">*</span>
          </Label>
          <Select
            value={(responses.division as string) || ''}
            onValueChange={(val) => setVal('division', val)}
          >
            <SelectTrigger id="division">
              <SelectValue placeholder="Select your division…" />
            </SelectTrigger>
            <SelectContent>
              {pricing.map((p) => (
                <SelectItem key={p.id} value={p.division}>
                  {p.division}{p.base_price > 0 ? ` — ${fmt(p.base_price)}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Dynamic fields (skip 'division' — handled above from pricing) */}
      {fields.filter((f) => f.key !== 'division').map((f) => (
        <div key={f.key} className="space-y-1.5">
          <Label htmlFor={f.key}>
            {f.label}
            {f.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          {renderField(f)}
          {f.helper_text && <p className="text-xs text-muted-foreground">{f.helper_text}</p>}
        </div>
      ))}

      <div className="flex items-center gap-2">
        <Checkbox
          id="hideFromPublic"
          checked={hideFromPublic}
          onCheckedChange={(c) => setHideFromPublic(c === true)}
        />
        <Label htmlFor="hideFromPublic" className="text-sm font-normal cursor-pointer">
          Hide my attendance from the public event page
        </Label>
      </div>

      <Button type="submit" disabled={submitting} className="w-full">
        {submitting ? (
          <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing…</>
        ) : totalDollars > 0 ? (
          `Register & Pay ${fmt(totalDollars)}`
        ) : (
          'Submit Registration'
        )}
      </Button>
    </form>
  );
};

export default CoachAttendanceForm;
